/**
 * TopPerformers - Top scorers, most starts, top assisters and most yellow cards widgets.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Target, Users, PlayCircle, Square } from 'lucide-react';

function RankedList({ items, valueKey, icon: Icon, iconColor, title, testId, emptyMessage = 'No stats recorded yet' }) {
  const filtered = items?.filter(p => p.stats?.[valueKey] > 0)?.slice(0, 5) || [];

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />{title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {filtered.length > 0 ? (
          <div className="flex flex-col gap-6">
            {filtered.map((player, index) => (
              <div key={player.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  index === 0 ? 'bg-amber-400 text-white' :
                  index === 1 ? 'bg-gray-400 text-white' :
                  index === 2 ? 'bg-orange-400 text-white' :
                  'bg-gray-200 text-gray-600'
                }`}>{index + 1}</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{player.name}</p>
                </div>
                <Badge variant="secondary" className="text-lg font-bold">{player.stats?.[valueKey] || 0}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center text-center py-10 px-4 text-gray-400"
            data-testid={`${testId}-empty`}
          >
            <Icon className="w-8 h-8 mb-2 opacity-40" strokeWidth={1.5} />
            <p className="text-sm font-medium text-gray-500">{emptyMessage}</p>
            <p className="text-xs mt-1 text-gray-400">It&apos;ll show here once a player gets on the board.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function TopPerformers({ topScorers, topAssisters, topStarts, topYellowCards }) {
  // If the season hasn't loaded any player data at all, render nothing.
  // Otherwise render all four widgets — each shows a friendly empty state
  // when no player has scored on that metric yet.
  const hasAnyData = (topScorers?.length || topAssisters?.length || topStarts?.length || topYellowCards?.length);
  if (!hasAnyData) return null;

  // Four columns on xl+, three on lg, two on md, single column on mobile.
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
      <RankedList
        items={topScorers}
        valueKey="goals"
        icon={Target}
        iconColor="text-emerald-600"
        title="Top Scorers"
        testId="top-scorers-list"
        emptyMessage="No goals recorded yet"
      />
      <RankedList
        items={topStarts}
        valueKey="starts"
        icon={PlayCircle}
        iconColor="text-amber-600"
        title="Most Starts"
        testId="most-starts-list"
        emptyMessage="No starts recorded yet"
      />
      <RankedList
        items={topAssisters}
        valueKey="assists"
        icon={Users}
        iconColor="text-blue-600"
        title="Top Assisters"
        testId="top-assisters-list"
        emptyMessage="No assists recorded yet"
      />
      <RankedList
        items={topYellowCards}
        valueKey="yellow_cards"
        icon={Square}
        iconColor="text-yellow-500 fill-yellow-400"
        title="Most Yellow Cards"
        testId="most-yellow-cards-list"
        emptyMessage="No yellow cards recorded yet"
      />
    </div>
  );
}
